import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import ProductCard from './ProductCard';
import ProductModal from './ProductModal';

interface Product {
  id: string;
  name: string;
  price: number;
  original_price?: number;
  image_url: string;
  description: string;
  category: string;
  stock: number;
  is_featured?: boolean;
  is_new?: boolean;
  is_on_sale?: boolean;
}

interface ProductGridProps {
  onAddToCart: (productId: string, quantity: number, productName: string, productPrice: number, productImage: string) => void;
}

export default function ProductGrid({ onAddToCart }: ProductGridProps) {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching products:', error);
        return;
      }

      // Transformar los datos de Supabase al formato esperado
      const transformedProducts = data?.map(product => ({
        id: product.id,
        name: product.name,
        price: product.price,
        originalPrice: product.original_price,
        image: product.image_url,
        description: product.description,
        category: product.category === 'carteras' ? 'Carteras' : 'Accesorios',
        stock: product.stock,
        isNew: product.is_new,
        isOnSale: product.is_on_sale,
        isFeatured: product.is_featured
      })) || [];

      setProducts(transformedProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar productos por categorías
  const featuredProducts = products.filter(product => product.isFeatured || product.isNew || product.isOnSale);
  const handbags = products.filter(product => product.category === 'Carteras');
  const accessories = products.filter(product => product.category === 'Accesorios');

  if (loading) {
    return (
      <div className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#C6A45C]"></div>
            <p className="text-gray-200 mt-4 font-thin" style={{ fontFamily: '"Lato", sans-serif' }}>
              CARGANDO PRODUCTOS...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-20 bg-black">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Productos Destacados */}
        {featuredProducts.length > 0 && (
          <section className="mb-20">
            <h2 className="text-3xl font-thin text-gray-200 text-center mb-12 uppercase tracking-wider" style={{ fontFamily: '"Lato", sans-serif' }}>
              PRODUCTOS DESTACADOS
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 px-4">
              {featuredProducts.map((product) => (
                <div key={product.id} className="transform transition-transform duration-300 hover:scale-102 max-w-80 mx-auto">
                  <ProductCard
                    product={product}
                    onAddToCart={onAddToCart}
                    onViewDetails={() => setSelectedProduct(product)}
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Carteras */}
        {handbags.length > 0 && (
          <section id="carteras" className="mb-20 scroll-mt-32">
            <h2 className="text-3xl font-thin text-gray-200 text-center mb-12 uppercase tracking-wider" style={{ fontFamily: '"Lato", sans-serif' }}>
              CARTERAS
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 px-4">
              {handbags.map((product) => (
                <div key={product.id} className="transform transition-transform duration-300 hover:scale-102 max-w-80 mx-auto">
                  <ProductCard
                    product={product}
                    onAddToCart={onAddToCart}
                    onViewDetails={() => setSelectedProduct(product)}
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Accesorios */}
        {accessories.length > 0 && (
          <section id="accesorios" className="mb-20 scroll-mt-32">
            <h2 className="text-3xl font-thin text-gray-200 text-center mb-12 uppercase tracking-wider" style={{ fontFamily: '"Lato", sans-serif' }}>
              ACCESORIOS
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 px-4">
              {accessories.map((product) => (
                <div key={product.id} className="transform transition-transform duration-300 hover:scale-102 max-w-80 mx-auto">
                  <ProductCard
                    product={product}
                    onAddToCart={onAddToCart}
                    onViewDetails={() => setSelectedProduct(product)}
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Mensaje cuando no hay productos */}
        {products.length === 0 && !loading && (
          <div className="text-center py-20">
            <div className="text-6xl text-gray-600 mb-6">
              <i className="ri-shopping-bag-line"></i>
            </div>
            <h3 className="text-2xl font-thin text-gray-200 mb-4 uppercase tracking-wider" style={{ fontFamily: '"Lato", sans-serif' }}>
              NO HAY PRODUCTOS DISPONIBLES
            </h3>
            <p className="text-gray-400 font-light" style={{ fontFamily: '"Lato", sans-serif' }}>
              Agrega productos desde el panel de administración para verlos aquí.
            </p>
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onAddToCart={onAddToCart}
        />
      )}
    </div>
  );
}
